/**
 * AdvancedEffectsModal Component
 * Modal wrapper for the Advanced Effects UI
 */

import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '../../ui/button';
import { X, Sparkles } from 'lucide-react';
import { AdvancedEffectsUI } from '../effects';

export const AdvancedEffectsModal = ({
  isOpen,
  onClose,
  tracks,
  selectedEffectsTrackId,
  advancedEffectsData,
  setAdvancedEffectsData,
  updateTrack,
  isPro,
  isStudio,
  setShowEffectPresetsBrowser,
}) => {
  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm"
          onClick={onClose}
        >
          <motion.div
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.95, opacity: 0 }}
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-xl max-h-[85vh] bg-zinc-900 rounded-2xl border border-zinc-700 shadow-2xl overflow-hidden flex flex-col"
            data-testid="advanced-effects-modal"
          >
            {/* Header */}
            <div className="px-6 py-4 bg-gradient-to-r from-orange-500/20 via-amber-500/20 to-yellow-500/20 border-b border-zinc-700 flex items-center justify-between">
              <div>
                <h2 className="text-lg font-bold text-white">Advanced Effects</h2>
                <p className="text-sm text-zinc-400">
                  {tracks.find(t => t.id === selectedEffectsTrackId)?.name || 'Track'} Effects Chain
                </p>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowEffectPresetsBrowser(true)}
                  className="border-amber-500/50 text-amber-400 hover:bg-amber-500/10"
                  data-testid="browse-presets-btn"
                >
                  <Sparkles className="w-4 h-4 mr-1" />
                  Browse Presets
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={onClose}
                  className="p-2 text-zinc-400 hover:text-white"
                >
                  <X className="w-5 h-5" />
                </Button>
              </div>
            </div>
            
            {/* Content */}
            <div className="flex-1 overflow-y-auto p-6">
              <AdvancedEffectsUI
                effects={advancedEffectsData || {}}
                onChange={(newEffects) => {
                  setAdvancedEffectsData(newEffects);
                  if (selectedEffectsTrackId) {
                    updateTrack(selectedEffectsTrackId, { advancedEffects: newEffects });
                  }
                }}
                isPro={isPro || isStudio}
                onUpgrade={() => {
                  onClose();
                  // Navigate to subscription page or show upgrade modal
                }}
              />
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default AdvancedEffectsModal;
